// DisclosuresPage — All 4 legal documents (Terms, Privacy, Risk, Cookies)
// Plus risk warning, regulatory info, jurisdiction (Malta)

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

interface Legal {
  id: string;
  title: string;
  description: string;
  effective: string;
  pages: number;
  readTimeMin: number;
  icon: string;
  color: string;
  keyPoints: string[];
}

const DOCS: Legal[] = [
  {
    id: 'terms', title: 'Terms & Conditions', effective: '2026-06-01', pages: 18, readTimeMin: 22, icon: '📋', color: '#007AFF',
    description: 'The agreement governing your use of Funder Pro evaluation programs, funded accounts, and any services.',
    keyPoints: ['Malta jurisdiction (C 104558)', '12.1 80% / 90% profit share', '13.1-13.20 Trading rules', '24h response SLA'],
  },
  {
    id: 'privacy', title: 'Privacy Notice', effective: '2026-05-15', pages: 14, readTimeMin: 17, icon: '🔒', color: '#34C759',
    description: 'How we collect, use, store, and share your personal data under GDPR (EU/UK/EEA).',
    keyPoints: ['GDPR-compliant data processing', 'Right to erasure (Art. 17)', 'Data retention 7 years', 'DPO contact info'],
  },
  {
    id: 'risk', title: 'Risk Disclosure', effective: '2026-04-22', pages: 7, readTimeMin: 9, icon: '⚠️', color: '#FF9500',
    description: 'CFD and futures trading involves significant risk. You may lose all capital. Past performance is not indicative.',
    keyPoints: ['CFDs are complex instruments', '74% of retail accounts lose money', 'Leverage amplifies losses', 'You should not invest more than you can afford to lose'],
  },
  {
    id: 'cookies', title: 'Cookie Notice', effective: '2026-05-01', pages: 5, readTimeMin: 6, icon: '🍪', color: '#5856D6',
    description: 'We use cookies and similar technologies for authentication, analytics, and personalisation.',
    keyPoints: ['Essential cookies (no consent needed)', 'Analytics cookies (opt-in)', 'Marketing cookies (opt-in)', 'Manage preferences anytime'],
  },
  {
    id: 'risk-disclaimer', title: 'Risk Disclaimer', effective: '2026-04-22', pages: 4, readTimeMin: 5, icon: '⛔', color: '#FF3B30',
    description: 'FunderPro Ltd. is not a financial advisor. We do not provide investment advice. All trading decisions are yours.',
    keyPoints: ['No investment advice', 'Not a brokerage', 'Not a financial institution', 'You trade at your own risk'],
  },
  {
    id: 'order-execution', title: 'Order Execution Policy', effective: '2026-04-22', pages: 6, readTimeMin: 8, icon: '📑', color: '#1C1C1E',
    description: 'How we route and execute your orders. Best execution policy under MiFID II.',
    keyPoints: ['Best execution obligation', 'Slippage may occur', 'Market vs limit orders', 'Execution venue disclosure'],
  },
];

const SUMMARY_BOXES = [
  { label: 'Jurisdiction',    value: 'Malta (EU)',   sub: 'FunderPro Ltd. C 104558', icon: '🇲🇹' },
  { label: 'Regulation',      value: 'MFSA',          sub: 'Malta Financial Services Authority', icon: '🏛️' },
  { label: 'Risk profile',    value: 'High risk',     sub: 'CFD trading · 74% retail loss',     icon: '⚠️' },
  { label: 'Customer support',value: '24/7',          sub: 'Live chat · email · phone',          icon: '💬' },
];

export function DisclosuresPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<'all' | 'regulatory' | 'risk' | 'privacy'>('all');
  const [viewing, setViewing] = useState<Legal | null>(null);

  const filtered = DOCS.filter((d) => {
    if (filter === 'all') return true;
    if (filter === 'regulatory') return ['terms', 'order-execution'].includes(d.id);
    if (filter === 'risk') return ['risk', 'risk-disclaimer'].includes(d.id);
    if (filter === 'privacy') return ['privacy', 'cookies'].includes(d.id);
    return true;
  });

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => viewing ? setViewing(null) : nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Disclosures</div>
        <div className="w-9" />
      </div>

      {!viewing && (
        <>
          {/* Hero — white floating card with docs summary + pill buttons */}
          <div className="mx-4 mt-4">
            <TfCardFloating className="text-center">
              <div className="text-[13px]" style={{ color: '#8E8E93' }}>Legal · Compliance · Transparency</div>
              <div className="mt-1 flex items-baseline justify-center gap-1">
                <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>{DOCS.length}</span>
                <span className="text-[15px] text-tf-text-3">documents</span>
              </div>
              <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
                <span className="text-tf-text-3">All regulatory documents, terms, and risk warnings</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button onClick={() => setFilter('risk')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                  Risk
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17L17 7" /><path d="M7 7h10v10" />
                  </svg>
                </button>
                <button onClick={() => nav('/portal/messages')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                  Ask legal
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 7L7 17" /><path d="M17 17H7V7" />
                  </svg>
                </button>
              </div>
            </TfCardFloating>
          </div>

          {/* 3-up stat row */}
          <TfStatRow
            className="mt-4"
            stats={[
              { label: 'Documents', value: `${DOCS.length}`, color: '#007AFF' },
              { label: 'Jurisdiction', value: 'Malta', color: '#1C1C1E' },
              { label: 'Risk level', value: 'High', color: '#FF3B30' },
            ]}
          />

          {/* Quick facts */}
          <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
            {SUMMARY_BOXES.map((s) => (
              <TfCard key={s.label} className="p-3 flex items-center gap-2.5">
                <span className="text-[24px]">{s.icon}</span>
                <div>
                  <div className="text-[10px] uppercase text-tf-text-3">{s.label}</div>
                  <div className="text-[13px] font-bold text-tf-text-1">{s.value}</div>
                  <div className="text-[9px] text-tf-text-3">{s.sub}</div>
                </div>
              </TfCard>
            ))}
          </div>

          {/* Filters */}
          <div className="px-4 pt-4 flex items-center gap-1.5 overflow-x-auto">
            {(['all', 'regulatory', 'risk', 'privacy'] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`disc-filter-${f}`}
                className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
                  filter === f ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
                ].join(' ')}
              >{f}</button>
            ))}
          </div>

          {/* Document list */}
          <TfSectionHeaderView title="Documents" viewAllLabel="View all" onViewAll={() => {}} />
          <div className="mx-4 space-y-2.5">
            {filtered.map((d) => (
              <button
                key={d.id} onClick={() => setViewing(d)}
                data-testid={`doc-${d.id}`}
                className="w-full rounded-[16px] bg-white p-4 text-left active:scale-[0.99] transition"
                style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] text-[22px]" style={{ backgroundColor: d.color + '15' }}>{d.icon}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-bold text-tf-text-1">{d.title}</h3>
                    <p className="text-[12px] text-tf-text-3 mt-0.5">{d.description}</p>
                    <div className="mt-1 text-[10px] text-tf-text-3">Effective {d.effective} · {d.pages} pages · {d.readTimeMin} min read</div>
                  </div>
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-text-muted mt-1" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {viewing && <DocumentView doc={viewing} onBack={() => setViewing(null)} />}
    </div>
  );
}

function DocumentView({ doc, onBack }: { doc: Legal; onBack: () => void }) {
  const nav = useNavigate();
  return (
    <div className="bg-white min-h-screen">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={onBack} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">{doc.title}</div>
        <button className="px-3 text-[15px] text-tf-blue">PDF</button>
      </div>

      <div className="px-4 py-4">
        <div className="rounded-[16px] bg-tf-surface-1 p-4 flex items-center gap-3 mb-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-[14px] text-[28px]" style={{ backgroundColor: doc.color + '15' }}>{doc.icon}</div>
          <div className="flex-1">
            <h1 className="text-[20px] font-bold text-tf-text-1">{doc.title}</h1>
            <p className="text-[12px] text-tf-text-3 mt-0.5">Effective {doc.effective} · {doc.pages} pages</p>
          </div>
        </div>

        <h2 className="text-[13px] font-normal uppercase tracking-wide text-tf-text-3 mb-2">Key points</h2>
        <div className="rounded-[14px] border border-tf-border bg-white p-4 space-y-2">
          {doc.keyPoints.map((p, i) => (
            <div key={i} data-testid={`kp-${i}`} className="flex items-start gap-2.5">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tf-blue-tint text-tf-blue text-[10px] font-bold">{i + 1}</div>
              <p className="text-[13px] text-tf-text-1 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>

        <h2 className="text-[13px] font-normal uppercase tracking-wide text-tf-text-3 mb-2 mt-5">Full document</h2>
        <div className="rounded-[14px] border border-tf-border bg-white p-4">
          <p className="text-[13px] text-tf-text-2 leading-relaxed mb-3">{doc.description}</p>
          <p className="text-[13px] text-tf-text-2 leading-relaxed mb-3">
            This document forms the complete agreement between you ("Trader" or "User") and FunderPro Ltd., a company incorporated in Malta with registration number C 104558, having its registered office at Cali House, Level 3, Vjal ir-Riħan, San Gwann, SGN 9020, Malta ("Funder Pro", "we", "us").
          </p>
          <p className="text-[13px] text-tf-text-2 leading-relaxed mb-3">
            By accessing or using our services, you confirm that you have read, understood, and agree to be bound by these terms. If you do not agree, you must not use our services.
          </p>
          <p className="text-[13px] text-tf-text-2 leading-relaxed">
            <span className="font-bold">Read the full {doc.title.toLowerCase()}</span> in the linked PDF for the complete legal text including all definitions, obligations, limitations of liability, and dispute resolution mechanisms.
          </p>
        </div>

        <div className="mt-4 flex gap-2">
          <button onClick={() => alert('Downloaded')} data-testid="download-doc" className="flex-1 h-11 rounded-full bg-tf-text-1 text-white text-[13px] font-semibold active:opacity-40">Download PDF</button>
          <button onClick={() => nav('/portal/messages')} data-testid="ask-legal" className="flex-1 h-11 rounded-full bg-tf-surface-1 text-tf-text-1 text-[13px] font-semibold active:opacity-40">Ask legal</button>
        </div>
      </div>
    </div>
  );
}
